import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * ProjectWorkspace - Componente Raiz
 * Layout que alterna entre coluna e linha baseado no breakpoint md.
 */
const ProjectWorkspace = React.forwardRef(({ className, isStudent, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace', 'flex w-full flex-col md:h-screen md:flex-row', isStudent && 'md:ml-16', className)}
    {...props}
  />
));
ProjectWorkspace.displayName = 'ProjectWorkspace';

/**
 * ProjectWorkspaceSidebar
 */
const ProjectWorkspaceSidebar = React.forwardRef(({ className, withMainNav, ...props }, ref) => (
  <aside
    ref={ref}
    className={cn(
      'project-workspace-sidebar',
      'scrollbar-hide flex-shrink-0 overflow-y-auto overflow-x-hidden bg-[#FEFEFE] p-8 text-[12px] leading-tight text-primary-dark shadow-[1px_0_8px_rgba(0,0,0,0.04)]',
      'w-full md:h-screen md:w-[220px]',
      withMainNav ? 'pt-[150px] md:pt-10' : 'pt-8',
      className
    )}
    {...props}
  />
));
ProjectWorkspaceSidebar.displayName = 'ProjectWorkspaceSidebar';

/**
 * ProjectWorkspaceQuestion
 */
const ProjectWorkspaceQuestion = React.forwardRef(({ className, isSelected, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'project-workspace-question',
      'group flex w-[calc(100%+32px)] appearance-none items-center justify-between gap-3 border-0 bg-transparent p-4 px-6 text-left transition-colors',
      isSelected && 'rounded-[5px_0_0_5px] bg-primary text-white',
      className
    )}
    {...props}
  />
));
ProjectWorkspaceQuestion.displayName = 'ProjectWorkspaceQuestion';

/**
 * ProjectWorkspaceContent
 */
const ProjectWorkspaceContent = React.forwardRef(({ className, withMainNav, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-content', 'relative flex-grow overflow-y-auto overflow-x-hidden', 'p-[52px_min(4vw,52px)]', 'w-full md:h-screen', withMainNav && 'pt-[105px]', className)}
    {...props}
  />
));
ProjectWorkspaceContent.displayName = 'ProjectWorkspaceContent';

const ProjectWorkspaceBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-body', className)}
    {...props}
  />
));
ProjectWorkspaceBody.displayName = 'ProjectWorkspaceBody';

/**
 * ProjectWorkspaceHeader
 */
const ProjectWorkspaceHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('project-workspace-header', 'mb-8 flex items-center gap-3 border-b border-solid border-[#EEEBF9] pb-3', className)}
    {...props}
  />
));
ProjectWorkspaceHeader.displayName = 'ProjectWorkspaceHeader';

const ProjectWorkspaceHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-header-content', 'min-w-0 grow', className)}
    {...props}
  />
));
ProjectWorkspaceHeaderContent.displayName = 'ProjectWorkspaceHeaderContent';

/**
 * ProjectWorkspaceTitle
 */
const ProjectWorkspaceTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('project-workspace-title', 'm-0 text-base font-bold leading-tight text-primary-dark', className)}
    {...props}
  />
));
ProjectWorkspaceTitle.displayName = 'ProjectWorkspaceTitle';

/**
 * ProjectWorkspaceSubtitle
 */
const ProjectWorkspaceSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('project-workspace-subtitle', 'mb-5 text-[14px] font-light text-neutral', className)}
    {...props}
  />
));
ProjectWorkspaceSubtitle.displayName = 'ProjectWorkspaceSubtitle';

/**
 * ProjectWorkspacePage
 */
const ProjectWorkspacePage = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-page', 'flex w-full flex-wrap justify-between gap-11', className)}
    {...props}
  />
));
ProjectWorkspacePage.displayName = 'ProjectWorkspacePage';

/**
 * ProjectWorkspaceCol
 */
const ProjectWorkspaceCol = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-col', 'mb-11 min-w-[270px] flex-1 basis-0 md:mb-0', className)}
    {...props}
  />
));
ProjectWorkspaceCol.displayName = 'ProjectWorkspaceCol';

/**
 * ProjectWorkspaceInfo
 */
const ProjectWorkspaceInfo = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'project-workspace-info',
      'sticky top-0 border border-solid border-primary-lightest bg-white p-3 text-black',
      '[&_p]:mb-5 [&_p]:text-[12px] [&_p]:leading-tight',
      '[&_p:not(:last-child)]:border-b [&_p:not(:last-child)]:border-primary-lightest [&_p:not(:last-child)]:pb-5',
      className
    )}
    {...props}
  />
));
ProjectWorkspaceInfo.displayName = 'ProjectWorkspaceInfo';

/**
 * ProjectWorkspaceWidget
 */
const ProjectWorkspaceWidget = React.forwardRef(({ className, ...props }, ref) => (
  <section
    ref={ref}
    className={cn('project-workspace-widget', 'mb-9 last:mb-0', className)}
    {...props}
  />
));
ProjectWorkspaceWidget.displayName = 'ProjectWorkspaceWidget';

const ProjectWorkspaceWidgetHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-widget-header', 'mb-2.5 flex items-center gap-5 rounded-[4px] border border-solid border-neutral-lightest p-2.5', className)}
    {...props}
  />
));
ProjectWorkspaceWidgetHeader.displayName = 'ProjectWorkspaceWidgetHeader';

const ProjectWorkspaceWidgetCard = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-widget-card', 'mb-2.5 flex flex-wrap items-center gap-3 rounded-[4px] bg-white p-4 shadow-dropshadow last:mb-0', className)}
    {...props}
  />
));
ProjectWorkspaceWidgetCard.displayName = 'ProjectWorkspaceWidgetCard';

const ProjectWorkspaceWidgetCardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('project-workspace-widget-card-header', 'mb-0 grow basis-0 text-sm font-light text-primary-dark', className)}
    {...props}
  />
));
ProjectWorkspaceWidgetCardHeader.displayName = 'ProjectWorkspaceWidgetCardHeader';

const ProjectWorkspaceWidgetCardBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-widget-card-body', 'grow basis-0', className)}
    {...props}
  />
));
ProjectWorkspaceWidgetCardBody.displayName = 'ProjectWorkspaceWidgetCardBody';

const ProjectWorkspaceWidgetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn('project-workspace-widget-title', 'mb-0 w-0 flex-grow text-xs font-bold text-primary-dark', className)}
    {...props}
  />
));
ProjectWorkspaceWidgetTitle.displayName = 'ProjectWorkspaceWidgetTitle';

const ProjectWorkspaceDateTimeWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-date-time-wrapper', 'mt-1 flex items-center gap-2', className)}
    {...props}
  />
));
ProjectWorkspaceDateTimeWrapper.displayName = 'ProjectWorkspaceDateTimeWrapper';

const ProjectWorkspaceDateTime = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('project-workspace-date-time', 'text-[12px] font-light text-neutral-darker', className)}
    {...props}
  />
));
ProjectWorkspaceDateTime.displayName = 'ProjectWorkspaceDateTime';

const ProjectWorkspaceAside = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('project-workspace-aside', 'flex-[0_0_100%] md:flex-[0_0_240px]', className)}
    {...props}
  />
));
ProjectWorkspaceAside.displayName = 'ProjectWorkspaceAside';

export {
  ProjectWorkspace,
  ProjectWorkspaceSidebar,
  ProjectWorkspaceQuestion,
  ProjectWorkspaceContent,
  ProjectWorkspaceBody,
  ProjectWorkspaceHeader,
  ProjectWorkspaceHeaderContent,
  ProjectWorkspaceTitle,
  ProjectWorkspaceSubtitle,
  ProjectWorkspacePage,
  ProjectWorkspaceCol,
  ProjectWorkspaceInfo,
  ProjectWorkspaceWidget,
  ProjectWorkspaceWidgetHeader,
  ProjectWorkspaceWidgetCard,
  ProjectWorkspaceWidgetCardHeader,
  ProjectWorkspaceWidgetCardBody,
  ProjectWorkspaceWidgetTitle,
  ProjectWorkspaceDateTimeWrapper,
  ProjectWorkspaceDateTime,
  ProjectWorkspaceAside
};
